import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Field, Select } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import Switch from "../../components/Switch";
import { useConfirm } from "../../components/ConfirmDialog";

const NOW = new Date();
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mo] = m.split("-");
  const names = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  return `${names[Number(mo) - 1]} ${y}`;
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export default function AdminCurrencies() {
  const { confirm } = useConfirm();
  const [rates, setRates] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({ currency: "", month: monthKey(NOW), rate: "" });
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadSettings = useCallback(async () => {
    try { const r = await api.get("/admin/currency-settings"); setSettings(r.data); } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/exchange-rates");
      setRates(r.data.rates || []);
      setCurrencies(r.data.currencies || []);
      setForm((f) => ({ ...f, currency: f.currency || (r.data.currencies?.[0]?.code || "") }));
    } catch { toast.error("Erreur de chargement"); }
  }, []);
  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.currency) return toast.error("Choisissez une devise");
    const rate = parseFloat(form.rate);
    if (!rate || rate <= 0) return toast.error("Le taux doit être supérieur à 0");
    setSaving(true);
    try {
      await api.post("/admin/exchange-rates", { currency: form.currency, month: form.month, rate });
      toast.success("Taux enregistré");
      setForm((f) => ({ ...f, rate: "" }));
      load(); loadSettings();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (rid) => {
    if (!(await confirm({ title: "Supprimer ce taux ?", confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/exchange-rates/${rid}`); toast.success("Taux supprimé"); load(); loadSettings(); }
    catch { toast.error("Erreur"); }
  };

  const editRate = (r) => setForm({ currency: r.currency, month: r.month, rate: String(r.rate) });

  const toggleActive = async (code) => {
    const cur = settings?.active_currencies || [];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    try { const r = await api.put("/admin/currency-settings", { active_currencies: next }); setSettings(r.data); }
    catch { toast.error("Erreur"); }
  };
  const toggleReminder = async () => {
    try { const r = await api.put("/admin/currency-settings", { reminder_enabled: !settings?.reminder_enabled }); setSettings(r.data); toast.success(r.data.reminder_enabled ? "Rappel activé" : "Rappel désactivé"); }
    catch { toast.error("Erreur"); }
  };

  const symbol = (code) => currencies.find((c) => c.code === code)?.symbol || code;
  const foreign = settings?.foreign_currencies || currencies;
  const missing = settings?.missing_this_month || [];

  return (
    <div data-testid="admin-currencies">
      <PageTitle overline="Comptabilité" title="Devises & Taux de change" />

      {settings?.reminder_enabled && missing.length > 0 && (
        <div className="bg-rouge-50 border border-rouge/30 rounded-sm p-4 mb-6 flex gap-3" data-testid="currency-missing-banner">
          <Icons.AlertTriangle size={16} className="text-rouge shrink-0 mt-0.5" />
          <p className="text-sm text-rouge-texte">
            Taux à renseigner pour <strong>{monthLabel(settings.current_month)}</strong> : {missing.join(", ")}. Saisissez-les ci-dessous pour convertir correctement vos factures et KPI.
          </p>
        </div>
      )}

      <div className="bg-marine-50 border border-marine/15 rounded-sm p-4 mb-6 flex gap-3 text-sm text-marine">
        <Icons.Info size={16} className="shrink-0 mt-0.5" />
        <p>Renseignez un <strong>taux de conversion mensuel</strong> pour chaque devise étrangère (1 unité de devise = X €). Les factures et paiements en devise sont convertis en euros dans le Cockpit financier au taux du <strong>mois de l'échéance</strong>. L'euro (€) est la devise de référence — aucun taux à saisir.</p>
      </div>

      {/* Réglages : devises actives + rappel mensuel */}
      <div className="bg-sable border border-marine/10 rounded-sm p-5 mb-8" data-testid="currency-settings">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-lg text-marine mb-1">Devises actives & rappel mensuel</h2>
            <p className="text-sm text-marine/80 mb-3">Cochez les devises que vous utilisez : un rappel mensuel (notification + tâche) vous invitera à saisir leur taux si celui du mois courant manque.</p>
            <div className="flex flex-wrap gap-2">
              {foreign.map((c) => {
                const on = (settings?.active_currencies || []).includes(c.code);
                return (
                  <button key={c.code} onClick={() => toggleActive(c.code)} data-testid={`active-currency-${c.code}`}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm border text-sm transition-colors ${on ? "bg-marine text-white border-marine" : "bg-sable text-marine border-marine/25 hover:bg-marine-50"}`}>
                    {on ? <Icons.CheckSquare size={16} /> : <Icons.Square size={16} />} {c.code} — {c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0" data-testid="reminder-toggle">
            <span className="text-sm text-marine/80">Rappel mensuel</span>
            <Switch checked={!!settings?.reminder_enabled} onChange={toggleReminder} title="Rappel mensuel" />
          </label>
        </div>
      </div>

      <div className="bg-sable border border-marine/10 rounded-sm p-5 mb-8">
        <h2 className="font-display text-lg text-marine mb-4">Ajouter / mettre à jour un taux</h2>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <Select label="Devise" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })}
            options={currencies.map((c) => ({ v: c.code, l: `${c.symbol} ${c.code} — ${c.label}` }))} testid="rate-currency" />
          <Field label="Mois">
            <input aria-label="Mois du taux de change" type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="rate-month" className="inp" />
          </Field>
          <Field label={`Taux (1 ${form.currency || "?"} = ? €)`}>
            <input type="number" step="0.0001" min="0" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="rate-value" className="inp" placeholder="Ex : 0,25" />
          </Field>
          <button disabled={saving} data-testid="rate-submit" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>
        <p className="text-[11px] text-marine/80 mt-2">Astuce : pour <strong>modifier un taux passé</strong>, saisissez la même devise et le même mois — le taux sera mis à jour (la date de dernière modification est conservée).</p>
      </div>

      <div className="bg-sable border border-marine/10 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-marine/10 bg-creme">
          <h2 className="font-display text-lg text-marine">Taux enregistrés</h2>
        </div>
        {rates.length === 0 ? (
          <p className="text-sm text-marine/80 p-5">Aucun taux enregistré pour le moment.</p>
        ) : (
          // Trop large pour un téléphone : le tableau défile dans sa carte, jamais la page (relative : le titre caché
          // « Actions » des lecteurs d'écran reste dedans — il poussait la page de 134 px à 390, mesuré le 15/10).
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm" data-testid="rates-table">
              <thead>
                <tr className="text-left text-marine/80 border-b border-marine/10 bg-sable">
                  <th className="px-5 py-3 text-xs uppercase tracking-wide font-medium text-left">Devise</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wide font-medium text-left">Mois</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wide font-medium text-left">Taux (→ €)</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wide font-medium text-left">Exemple</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-wide font-medium text-left">Dernière modif.</th>
                  <th className="px-5 py-3 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.id} data-testid={`rate-row-${r.id}`} className="border-b border-marine/5 last:border-0 bg-creme hover:bg-sable transition-colors">
                    <td className="px-5 py-3 font-medium text-marine">{symbol(r.currency) !== r.currency ? `${symbol(r.currency)} ` : ""}{r.currency}</td>
                    <td className="px-5 py-3 text-marine/80">{monthLabel(r.month)}</td>
                    <td className="px-5 py-3 text-marine/80">1 {r.currency} = {r.rate.toLocaleString("fr-FR", { maximumFractionDigits: 4 })} €</td>
                    <td className="px-5 py-3 text-marine/80">1 000 {r.currency} = {(1000 * r.rate).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</td>
                    <td className="px-5 py-3 text-marine/80 text-xs">{fmtDate(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ""}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => editRate(r)} data-testid={`rate-edit-${r.id}`} title="Modifier ce taux" className="text-marine hover:bg-marine/10 rounded-sm p-1.5 mr-1"><Icons.Pencil size={16} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`rate-delete-${r.id}`} title="Supprimer" className="text-rouge hover:bg-rouge/10 rounded-sm p-1.5"><Icons.Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
